import { useState } from "react";
import { ShieldCheck, ShieldPlus, Send, Trash2, Info } from "lucide-react";
import { Avatar, Panel, EmptyState, inputCls, btnPrimary, relTime } from "./crm";
import { TempCodeDialog } from "./AgentsView";

const STATUS = {
  invited: { label: "Invited · awaiting first sign-in", cls: "border-amber-200 bg-amber-50 text-amber-700" },
  active: { label: "Active", cls: "border-emerald-200 bg-emerald-50 text-emerald-700" },
};

function AdminRow({ admin, isMe, canRemove, inviting, confirm, onInvite, onConfirm, onRemove }) {
  const st = STATUS[admin.login_status] || STATUS.active;
  return (
    <li className="flex flex-col gap-3 px-4 py-4 sm:flex-row sm:items-center sm:px-5" data-testid={`admin-row-${admin.id}`}>
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <Avatar name={admin.name} size="md" />
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="truncate text-[15px] font-semibold text-slate-900">{admin.name}</span>
            {isMe && <span className="rounded-md bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-slate-500" data-testid="admin-row-you">You</span>}
          </div>
          <div className="truncate text-xs text-slate-500">{admin.email}</div>
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-[11px]">
            <span className={`inline-flex items-center rounded-md border px-2 py-0.5 font-semibold ${st.cls}`} data-testid={`admin-status-${admin.id}`}>{st.label}</span>
            <span className="text-slate-400">{admin.last_login_at ? `Last sign-in ${relTime(admin.last_login_at)}` : admin.invited_by ? `Invited by ${admin.invited_by}` : "Original admin"}</span>
          </div>
        </div>
      </div>
      {!isMe && (
        <div className="flex items-center gap-1.5 sm:justify-end">
          <button onClick={() => onInvite(admin)} disabled={inviting} data-testid={`admin-invite-${admin.id}`} className="inline-flex min-h-[40px] sm:min-h-0 flex-1 sm:flex-none items-center justify-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:border-sky-300 hover:text-sky-700 disabled:opacity-50">
            <Send className="h-3 w-3" /> {inviting ? "Sending…" : "Resend invite"}
          </button>
          {confirm ? (
            <>
              <button onClick={() => onRemove(admin)} data-testid={`admin-confirm-delete-${admin.id}`} className="min-h-[40px] sm:min-h-0 rounded-md bg-rose-600 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-rose-700">Remove</button>
              <button onClick={() => onConfirm(null)} data-testid={`admin-cancel-delete-${admin.id}`} className="min-h-[40px] sm:min-h-0 rounded-md border border-slate-200 px-2 py-1.5 text-xs font-semibold text-slate-600">Cancel</button>
            </>
          ) : (
            <button onClick={() => onConfirm(admin.id)} disabled={!canRemove} title={canRemove ? "Remove admin" : "At least one admin must remain"} data-testid={`admin-delete-${admin.id}`} className="inline-flex min-h-[40px] sm:min-h-0 items-center justify-center rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-slate-500 hover:border-rose-300 hover:text-rose-600 disabled:opacity-40"><Trash2 className="h-3.5 w-3.5" /></button>
          )}
        </div>
      )}
    </li>
  );
}

export default function AdminsView({ admins, me, onAdd, onInvite, onRemove }) {
  const [form, setForm] = useState({ name: "", email: "" });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [confirmId, setConfirmId] = useState(null);
  const [inviting, setInviting] = useState(null);
  const [tempInfo, setTempInfo] = useState(null);

  const showCode = (who, res) => { if (res && !res.invite_sent && res.temp_password) setTempInfo({ name: who.name, email: who.email, temp_password: res.temp_password }); };

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (form.name.trim().length < 2) errs.name = "Enter the admin's full name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) errs.email = "Enter a valid e-mail address.";
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setBusy(true);
    try {
      const res = await onAdd({ name: form.name.trim(), email: form.email.trim().toLowerCase() });
      if (res) { setForm({ name: "", email: "" }); showCode(res, res); }
    } finally { setBusy(false); }
  };

  const invite = async (admin) => {
    setInviting(admin.id);
    try { showCode(admin, await onInvite(admin)); } finally { setInviting(null); }
  };

  return (
    <div className="space-y-5" data-testid="admins-view">
      <div>
        <h1 className="text-xl sm:text-2xl font-extrabold tracking-tight text-slate-900">Admins</h1>
        <p className="mt-1 text-sm text-slate-500"><span className="font-mono-num font-semibold text-slate-900">{admins.length}</span> {admins.length === 1 ? "person has" : "people have"} full access to the recruiting CRM</p>
      </div>
      <div className="grid gap-4 xl:grid-cols-3">
        <Panel title="Invite an admin" subtitle="Full access: recruits, agents, routing and other admins" testid="admin-add-admin-form">
          <form onSubmit={submit} noValidate className="space-y-4 p-5">
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-slate-700">Full name</label>
              <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="admin-name-input" placeholder="e.g. Brandon Beal" className={`${inputCls} ${errors.name ? "border-rose-400" : ""}`} />
              {errors.name && <p className="mt-1 text-xs text-rose-600" data-testid="admin-name-error">{errors.name}</p>}
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-slate-700">E-mail</label>
              <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="admin-email-input" placeholder="name@breezewealthpartners.com" className={`${inputCls} ${errors.email ? "border-rose-400" : ""}`} />
              {errors.email && <p className="mt-1 text-xs text-rose-600" data-testid="admin-email-error">{errors.email}</p>}
            </div>
            <button type="submit" disabled={busy} data-testid="admin-add-admin-button" className={`${btnPrimary} w-full`}><ShieldPlus className="h-4 w-4" /> {busy ? "Inviting…" : "Send Admin Invite"}</button>
            <p className="flex items-start gap-2 text-[11px] text-slate-500"><Info className="mt-0.5 h-3.5 w-3.5 shrink-0" /> They receive an e-mail with a one-time access code for the admin console and must set their own password on first sign-in. Admins can do everything you can, including inviting or removing other admins.</p>
          </form>
        </Panel>
        <Panel title="Admin team" subtitle="Everyone with console access" className="order-first xl:order-none xl:col-span-2 overflow-hidden" testid="admin-admins-list">
          {admins.length === 0 ? <EmptyState icon={ShieldCheck} title="Loading admins…" hint="" /> : (
            <ul className="divide-y divide-slate-100">
              {admins.map((a) => (
                <AdminRow key={a.id} admin={a} isMe={a.id === me?.id} canRemove={admins.length > 1} inviting={inviting === a.id} confirm={confirmId === a.id}
                  onInvite={invite} onConfirm={setConfirmId} onRemove={(adm) => { onRemove(adm); setConfirmId(null); }} />
              ))}
            </ul>
          )}
        </Panel>
      </div>
      <TempCodeDialog info={tempInfo} onClose={() => setTempInfo(null)} portal="/admin" />
    </div>
  );
}
